<template>
    <StepShell :embedded="embedded">
        <h1 v-if="!embedded" class="greeting">Hi {{ firstName || "there" }}, I&rsquo;m Nadia.</h1>

        <div class="sections">
            <NadiaCard :title="title">
                <p class="card-body">{{ body }}</p>
                <StepPoints :points="points" />
            </NadiaCard>

            <section class="privacy">
                <div>
                    <h2 class="privacy-title">Private by default</h2>
                    <ul class="privacy-list">
                        <li v-for="promise in PRIVACY_PROMISES" :key="promise">{{ promise }}</li>
                    </ul>
                </div>
                <BaseButton theme="tertiary" size="sm" class="privacy-link" @click="privacyOpen = true">
                    Read the full privacy guarantee
                    <i aria-hidden="true" class="bi bi-arrow-up-right"></i>
                </BaseButton>
            </section>
        </div>

        <StepActions flush>
            <BaseButton theme="primary" @click="$emit('complete')">Next</BaseButton>
        </StepActions>

        <PrivacyNoticeDialog v-model:open="privacyOpen" />
    </StepShell>
</template>

<script setup lang="ts">
/**
 * Show privacy commitments alongside Nadia's introduction so users see them without advancing a carousel.
 */
import BaseButton from "~vue/components/BaseButton.vue";
import PrivacyNoticeDialog from "~vue/Onboarding/PrivacyNoticeDialog.vue";
import { computed, ref } from "vue";

import NadiaCard from "./NadiaCard.vue";
import StepActions from "./StepActions.vue";
import StepPoints from "./StepPoints.vue";
import StepShell from "./StepShell.vue";

const {
    firstName,
    managesPeople,
    embedded = false,
} = defineProps<{
    firstName: string;
    managesPeople: boolean;
    embedded?: boolean;
}>();
defineEmits<{ complete: [] }>();

const privacyOpen = ref(false);

const title = computed(() => (managesPeople ? "Managing is hard. I’m here to make it a little easier." : "Your job is hard. I’m here to make it a little easier."));
const body = computed(() =>
    managesPeople
        ? "I’m a coach who knows your company and your team, built for the parts of managing that don’t show up in any handbook."
        : "I’m a coach who knows your company and the people around you, built for the parts of work that don’t show up in any job description.",
);

const points = computed(() => [
    {
        icon: "bi-people-fill",
        lead: "Navigating complex relationships",
        text: managesPeople
            ? "The report who has checked out, the peer who keeps blocking you, the skip-level who wants an answer today."
            : "The teammate who blocks you, the manager who won’t decide, the peer whose priorities collide with yours.",
    },
    {
        icon: "bi-flag-fill",
        lead: "Setting goals worth hitting",
        text: managesPeople
            ? "For your team and for yourself, in the language your organization already recognizes."
            : "For your work and for yourself, in the language your organization already recognizes.",
    },
    {
        icon: "bi-megaphone-fill",
        lead: "Advocating for your career",
        text: "Making the case for your growth, especially when nobody else is going to make it for you.",
    },
]);

/* Keep these promises brief; PrivacyNoticeDialog contains the full privacy guarantees. */
const PRIVACY_PROMISES = [
    "Nobody at work can read your conversations.",
    "Nothing leaves unless you publish it yourself.",
    "Everything I know about you is on your profile — correct it any time.",
];
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .greeting {
        @apply display-serif text-center text-ink;
    }

    .sections {
        @apply flex flex-col gap-5;
    }

    .card-body {
        @apply body-small text-ink-muted;
    }

    .privacy {
        @apply flex flex-col items-start gap-2.5 rounded-lgr bg-surface-sunken p-5;
    }

    .privacy-title {
        @apply body-bold text-ink;
    }

    .privacy-list {
        @apply body-small mt-1 list-disc pl-5 text-ink-muted;
    }

    .privacy-link {
        @apply -ml-2 inline-flex items-center gap-2.5;
    }
}

/*
 * Keep this rule unlayered to override the base-btn-sm utility and make the privacy link less prominent
 * than Next.
 */
.privacy-link {
    @apply body-small font-normal text-ink-muted;
}
</style>
